<template>
  <div class="panel">
    <div class="flex justify-between mb-8">
      <h1 class="text-xl">Manage users</h1>
      <router-link :to="{ name: 'account/manage-users/create-user' }" class="btn btn-primary">
        <i class="fas fa-plus"></i> Create user
      </router-link>
    </div>

    <!-- Loading -->
    <div v-if="data.isLoading" class="flex flex-col min-h-[36rem] justify-center items-center">
      <FadeLoader :loading="data.isLoading" color="#E8E7EB" />
    </div>

    <!-- Users list -->
    <div v-else-if="data.users.length > 0">
      <Form>
        <ul>
          <li
              v-for="(user, index) in data.users"
              :key="user.id"
              class="mb-2 bg-grey-100 rounded px-5 py-3.5 font-bold text-sm flex flex-wrap md:flex-nowrap items-center"
          >
            <!-- Name + email -->
            <div class="w-full md:w-5/12 mb-2 md:mb-0">
              <div class="font-bold text-lg md:text-base">
                {{ user.name }}
              </div>
              <div class="text-xs text-gray-500">
                {{ user.email }}
              </div>
            </div>

            <!-- Role -->
            <div class="w-6/12 md:w-3/12 mb-2 md:mb-0">
              <span
                  v-if="user.account?.roles?.[0]?.name"
                  class="tag inline-flex bg-primary text-white rounded px-1.5 py-1 items-center text-tiny text-center mb-0"
              >
                {{ getRoleLabelFromName(user.account.roles[0].name) }}
              </span>
            </div>

            <!-- Actions -->
            <div class="w-6/12 md:w-4/12 flex justify-end gap-1">
              <button
                  v-if="user.id !== currentUserId"
                  type="button"
                  @click="deleteUser(user.id, index)"
                  title="Delete user"
                  class="ml-2 bg-red-200 text-white p-1 rounded-full text-md min-w-[32px] min-h-[32px]"
              >
                <i class="far fa-trash-alt" aria-label="Delete user"></i>
              </button>
            </div>
          </li>
        </ul>
      </Form>

      <!-- Pagination; component itself hides when only 1 page -->
      <Pagination :links="data.pagination" />
    </div>

    <!-- No users -->
    <div v-else>
      <div class="bg-secondary/10 border border-secondary px-6 py-12 rounded-2xl text-center">
        <h2 class="font-bold text-lg">No users found</h2>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onActivated } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useToast } from "vue-toast-notification";
import { useUserStore } from "../../stores/user";
import api from "../../api/axios-api";
import Form from "../Form/Form.vue";
import Pagination from "../Global/Pagination.vue";
import FadeLoader from "vue-spinner/src/FadeLoader.vue";

const userStore = useUserStore();
const currentUserId = userStore.getUser?.id;

const data = ref({
  id: "ManageUsers",
  users: [],
  pagination: [],
  lastPage: 1,
  isLoading: false,
});

const route = useRoute();
const router = useRouter();
const $toast = useToast();

const getUsers = async () => {
  try {
    data.value.isLoading = true;

    const page = Number(route.params.page || 1);

    const { data: res } = await api.get("admin/users", {
      params: { page }
    });

    if (res?.users) {
      const paginator = res.users;

      data.value.users = Array.isArray(paginator.data)
          ? paginator.data
          : [];

      data.value.pagination = Array.isArray(paginator.links)
          ? paginator.links
          : [];

      // ⭐ IMPORTANT: store total pages so delete logic knows what to do
      data.value.lastPage = Number(paginator.last_page || 1);
    } else {
      data.value.users = [];
      data.value.pagination = [];
      data.value.lastPage = 1;
    }
  } catch (error) {
    $toast.open({
      message: error.response?.data?.message || error.message,
      type: "error",
      duration: 100000,
      dismissible: false,
    });
  } finally {
    data.value.isLoading = false;
  }
};

const deleteUser = async (userId, index) => {
  if (!confirm("Do you really want to delete this user?")) return;

  try {
    const response = await api.delete(`admin/users/${userId}`);

    if (!response.data?.success) {
      $toast.open({
        message: response.data?.message || "Unable to delete user.",
        type: "error",
        duration: 100000,
        dismissible: false,
      });
      return;
    }

    // remove from current list first (visual)
    data.value.users.splice(index, 1);

    const currentPage = Number(route.params.page || 1);

    // If after deletion the page still has items → nothing else to do
    if (data.value.users.length > 0) return;

    // Otherwise fetch the updated paginator info
    await getUsers(); // this updates data.value.lastPage

    const lastPage = data.value.lastPage || 1;

    // If current page > last possible page, redirect to lastPage
    if (currentPage > lastPage) {
      router.push({
        name: "account/manage-users/paged",
        params: { page: lastPage }
      });
    }

  } catch (error) {
    console.error(error);
    $toast.open({
      message: error.response?.data?.message || error.message,
      type: "error",
      duration: 100000,
      dismissible: false,
    });
  }
};

const getRoleLabelFromName = (name) => String(name).replace(/_/g, " ");

// refresh when mounted or re-activated
onMounted(getUsers);
onActivated(getUsers);

// refresh when paginated route changes
watch(() => route.params.page, getUsers);
</script>
